import type { StyleRun } from '@rta/rta-blueprint-library';

import { normalizeStyleRuns } from './apply-style-run';

export type RemapStyleRunsParams = {
	runs: Array<StyleRun>;
	previousText: string;
	nextText: string;
};

/**
 * Re-indexes style runs after a text edit using a common-prefix/common-suffix diff, so runs outside the
 * edited span survive typing (#390). Runs are shifted, extended or clipped; only a run whose whole span was
 * deleted is dropped, so a run can never end up coloring characters the user did not choose.
 */
export const remapStyleRuns = (params: RemapStyleRunsParams): Array<StyleRun> => {
	const { runs, previousText, nextText } = params;
	if (previousText === nextText || runs.length === 0) return normalizeStyleRuns(runs);

	const maxCommon = Math.min(previousText.length, nextText.length);
	let prefix = 0;
	while (prefix < maxCommon && previousText[prefix] === nextText[prefix]) prefix += 1;
	let suffix = 0;
	while (suffix < maxCommon - prefix && previousText[previousText.length - 1 - suffix] === nextText[nextText.length - 1 - suffix]) suffix += 1;

	const removedStart = prefix;
	const removedEnd = previousText.length - suffix;
	const insertedLength = nextText.length - suffix - prefix;
	const delta = insertedLength - (removedEnd - removedStart);

	const remapped: Array<StyleRun> = [];
	runs.forEach((run) => {
		if (run.end < removedStart) {
			remapped.push({ ...run });
			return;
		}
		// a pure insertion at a run's right edge is absorbed by that run, matching Word/PowerPoint
		if (run.end === removedStart && removedEnd === removedStart) {
			remapped.push({ ...run, end: run.end + delta });
			return;
		}
		if (run.start >= removedEnd) {
			remapped.push({ ...run, start: run.start + delta, end: run.end + delta });
			return;
		}

		const newStart = Math.min(run.start, removedStart);
		const newEnd = run.end >= removedEnd ? run.end + delta : removedStart;
		if (newEnd > newStart) remapped.push({ ...run, start: newStart, end: newEnd });
	});

	return normalizeStyleRuns(remapped.map((run) => ({
		...run,
		start: Math.max(0, Math.min(run.start, nextText.length)),
		end: Math.max(0, Math.min(run.end, nextText.length))
	})));
};
